import { FormEvent, useEffect, useRef, useState } from 'react';

import Modal from '../common/Modal';
import Button from '../common/Button';
import Input from '../common/Input';
import { useToast } from '../../contexts/ToastContext';
import { useInvoiceBranding, useSaveInvoiceBranding } from '../../hooks/useInvoiceBranding';
import { UpsertInvoiceBrandingPayload } from '../../services/invoiceBrandingService';

// Keep in sync with the backend DTO cap (~750 KB image once base64-decoded).
const MAX_LOGO_BYTES = 750 * 1024;
const ACCEPTED_LOGO_TYPES = ['image/png', 'image/jpeg'];

// Company name: letters, spaces and common business punctuation (& . , ' ( ) / -).
// Kept in sync with the backend DTO. Digits and other special characters are
// stripped on type; at least one letter is required at submit.
const COMPANY_NAME_ALLOWED = /^[A-Za-z&.,'()/\- ]+$/;
const COMPANY_NAME_STRIP = /[^A-Za-z&.,'()/\- ]/g;

// Company address: alphanumeric with spaces and , . - (max 200). Kept in sync
// with the backend DTO. Disallowed characters are stripped on type.
const ADDRESS_ALLOWED = /^[A-Za-z0-9 ,.-]+$/;
const ADDRESS_STRIP = /[^A-Za-z0-9 ,.-]/g;

// GSTIN: 2-digit state code + 5 letters + 4 digits + 1 letter + 1 alphanumeric
// + Z + 1 alphanumeric. Kept in sync with the backend DTO.
const GSTIN_PATTERN = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[0-9A-Z]{3}$/;

// Indian mobile: 10 digits starting with 6-9. Kept in sync with the backend DTO.
const MOBILE_PATTERN = /^[6-9]\d{9}$/;

// Website: protocol optional, requires a dotted domain with a 2+ letter TLD and
// an optional path (accepts www.sdcm.in / example.com, rejects abc / 123 / test).
const WEBSITE_PATTERN = /^(https?:\/\/)?([\w-]+\.)+[a-zA-Z]{2,}(\/\S*)?$/;

interface FormState {
  companyName: string;
  companyTagline: string;
  companyAddress: string;
  gstNumber: string;
  contactNumber: string;
  email: string;
  website: string;
}

const EMPTY_FORM: FormState = {
  companyName: '',
  companyTagline: '',
  companyAddress: '',
  gstNumber: '',
  contactNumber: '',
  email: '',
  website: '',
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export default function InvoiceBrandingModal({ isOpen, onClose }: Props) {
  const { showToast } = useToast();
  const { data: branding, isLoading } = useInvoiceBranding();
  const saveMutation = useSaveInvoiceBranding();

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [logoBase64, setLogoBase64] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Populate the form once the saved branding loads.
  useEffect(() => {
    if (branding) {
      setForm({
        companyName: branding.companyName ?? '',
        companyTagline: branding.companyTagline ?? '',
        companyAddress: branding.companyAddress ?? '',
        gstNumber: branding.gstNumber ?? '',
        contactNumber: branding.contactNumber ?? '',
        email: branding.email ?? '',
        website: branding.website ?? '',
      });
      setLogoBase64(branding.logoBase64 ?? null);
    }
  }, [branding]);

  function handleField(field: keyof FormState, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
    // Clear the field's error as soon as the user edits it.
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  }

  // Validate every field and return the per-field error map (empty = valid).
  function validate(): Partial<Record<keyof FormState, string>> {
    const next: Partial<Record<keyof FormState, string>> = {};

    if (!form.companyName.trim()) {
      next.companyName = 'Company name is required';
    } else if (!COMPANY_NAME_ALLOWED.test(form.companyName.trim())) {
      next.companyName = "Company name can only contain letters, spaces and & . , ' ( ) / -";
    } else if (!/[A-Za-z]/.test(form.companyName)) {
      next.companyName = 'Company name must contain at least one letter';
    }

    if (!form.companyAddress.trim()) {
      next.companyAddress = 'Company address is required';
    } else if (!ADDRESS_ALLOWED.test(form.companyAddress.trim())) {
      next.companyAddress = 'Company address can only contain letters, numbers, spaces and , . -';
    }

    // GST is optional, but when supplied it must be a valid GSTIN.
    if (form.gstNumber.trim() && !GSTIN_PATTERN.test(form.gstNumber.trim())) {
      next.gstNumber = 'Enter a valid 15-character GSTIN (e.g. 27AAAAA0000A1Z5)';
    }

    // Website is optional, but when supplied it must be a valid URL.
    if (form.website.trim() && !WEBSITE_PATTERN.test(form.website.trim())) {
      next.website = 'Enter a valid website URL (e.g. https://example.com)';
    }

    if (!form.contactNumber.trim()) {
      next.contactNumber = 'Contact number is required';
    } else if (!MOBILE_PATTERN.test(form.contactNumber.trim())) {
      next.contactNumber = 'Enter a valid 10-digit mobile number starting with 6-9';
    }

    if (!form.email.trim()) {
      next.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      next.email = 'Enter a valid email address';
    }

    return next;
  }

  function handleLogoSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ACCEPTED_LOGO_TYPES.includes(file.type)) {
      showToast('Logo must be a PNG or JPEG image', 'error');
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      showToast('Logo is too large — please use an image under 750 KB', 'error');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => setLogoBase64(reader.result as string);
    reader.onerror = () => showToast('Could not read the selected image', 'error');
    reader.readAsDataURL(file);
  }

  function removeLogo() {
    setLogoBase64(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function triggerFilePicker() {
    fileInputRef.current?.click();
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      showToast('Please fix the highlighted fields', 'error');
      return;
    }
    setErrors({});

    const payload: UpsertInvoiceBrandingPayload = {
      logoBase64: logoBase64 ?? null,
      companyName: form.companyName.trim(),
      companyTagline: form.companyTagline.trim() || null,
      companyAddress: form.companyAddress.trim(),
      gstNumber: form.gstNumber.trim() || null,
      contactNumber: form.contactNumber.trim(),
      email: form.email.trim(),
      website: form.website.trim() || null,
    };

    try {
      await saveMutation.mutateAsync(payload);
      showToast('Invoice branding saved successfully', 'success');
      onClose();
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? err?.message ?? 'Failed to save invoice branding';
      showToast(msg, 'error');
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Invoice Branding"
      size="xl"
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            type="submit"
            onClick={handleSubmit as any}
            disabled={saveMutation.isPending || isLoading}
          >
            {saveMutation.isPending ? 'Saving...' : 'Save Branding'}
          </Button>
        </div>
      }
    >
      {/* Info banner */}
      <div className="flex items-start gap-3 mb-5 p-3 rounded-lg bg-brand-50 border border-brand-100">
        <i className="fas fa-info-circle text-brand-500 mt-0.5" />
        <p className="text-xs text-brand-800 leading-relaxed">
          These details appear in the header of every tax invoice generated from now on.
          Already-generated invoices keep the branding they were issued with.
        </p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12 text-gray-400 text-sm">
          <i className="fas fa-spinner fa-spin mr-2" /> Loading branding...
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Hidden native input — driven by the styled controls below */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg"
            onChange={handleLogoSelect}
            className="hidden"
          />

          {/* Logo section */}
          <section className="rounded-xl border-2 border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-gray-50 to-white border-b border-gray-200 px-4 py-2.5 flex items-center gap-2">
              <i className="fas fa-image text-brand-600" />
              <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Company Logo</span>
            </div>
            <div className="p-5">
              {logoBase64 ? (
                <div className="flex flex-col sm:flex-row sm:items-center gap-5">
                  <div className="flex items-center justify-center w-44 h-28 rounded-lg border border-gray-200 bg-white overflow-hidden shrink-0 shadow-sm">
                    <img src={logoBase64} alt="Company logo preview" className="max-w-full max-h-full object-contain" />
                  </div>
                  <div className="flex flex-col gap-2">
                    <button
                      type="button"
                      onClick={triggerFilePicker}
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-brand-700 bg-brand-50 rounded-lg hover:bg-brand-100 transition-colors"
                    >
                      <i className="fas fa-sync-alt" /> Change logo
                    </button>
                    <button
                      type="button"
                      onClick={removeLogo}
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <i className="fas fa-trash-alt" /> Remove logo
                    </button>
                    <p className="text-xs text-gray-400 mt-1">PNG or JPEG · up to 750 KB</p>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={triggerFilePicker}
                  className="w-full flex flex-col items-center justify-center gap-2 py-8 border-2 border-dashed border-gray-300 rounded-xl bg-gray-50 hover:border-brand-400 hover:bg-brand-50/50 transition-colors"
                >
                  <span className="w-12 h-12 rounded-full bg-brand-100 flex items-center justify-center">
                    <i className="fas fa-cloud-upload-alt text-brand-600 text-xl" />
                  </span>
                  <span className="text-sm font-medium text-gray-700">Click to upload your logo</span>
                  <span className="text-xs text-gray-400">PNG or JPEG · up to 750 KB · shown top-left of the invoice header</span>
                </button>
              )}
            </div>
          </section>

          {/* Company details section */}
          <section className="rounded-xl border-2 border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-gray-50 to-white border-b border-gray-200 px-4 py-2.5 flex items-center gap-2">
              <i className="fas fa-building text-brand-600" />
              <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Company Details</span>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Company Name"
                required
                icon="fa-signature"
                value={form.companyName}
                maxLength={255}
                error={errors.companyName}
                onChange={(e) => handleField('companyName', e.target.value.replace(COMPANY_NAME_STRIP, ''))}
              />
              <Input
                label="Tagline"
                value={form.companyTagline}
                maxLength={255}
                helperText="Optional line under the company name"
                onChange={(e) => handleField('companyTagline', e.target.value)}
              />
              <div className="md:col-span-2">
                <Input
                  label="Company Address"
                  required
                  icon="fa-location-dot"
                  value={form.companyAddress}
                  maxLength={200}
                  error={errors.companyAddress}
                  onChange={(e) => handleField('companyAddress', e.target.value.replace(ADDRESS_STRIP, '').slice(0, 200))}
                />
              </div>
              <Input
                label="GST Number"
                icon="fa-hashtag"
                value={form.gstNumber}
                maxLength={15}
                helperText="15-character GSTIN, e.g. 27AAAAA0000A1Z5 · leave blank if not applicable"
                error={errors.gstNumber}
                onChange={(e) => handleField('gstNumber', e.target.value.toUpperCase().replace(/[^0-9A-Z]/g, '').slice(0, 15))}
              />
              <Input
                label="Contact Number"
                required
                type="tel"
                icon="fa-phone"
                value={form.contactNumber}
                maxLength={10}
                error={errors.contactNumber}
                onChange={(e) => handleField('contactNumber', e.target.value.replace(/\D/g, '').slice(0, 10))}
              />
              <Input
                label="Email Address"
                required
                type="email"
                icon="fa-envelope"
                value={form.email}
                maxLength={255}
                error={errors.email}
                onChange={(e) => handleField('email', e.target.value)}
              />
              <Input
                label="Website"
                icon="fa-globe"
                value={form.website}
                maxLength={255}
                helperText="Optional"
                error={errors.website}
                onChange={(e) => handleField('website', e.target.value)}
              />
            </div>
          </section>
        </form>
      )}
    </Modal>
  );
}
